/* eslint-disable react/jsx-handler-names */
"use client";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import CustomImage from "../../atoms/customImage";
import { lockBody, unlockBody } from "../../../utils/document";
import { BOTTOM } from "../../../constants/common/common";
import Typography from "../../atoms/typography";
import { formatClassNames } from "../../../utils/common";
import { usePreCheckoutEmbedded } from "../../../customHooks/usePreCheckoutEmbedded";
import { ArrowLeftIcon } from "@phosphor-icons/react";

/**
 * Custom Drawer Component.
 * @param {Object} props - Component props.
 * @param {string} props.position - Position of the drawer ('bottom', 'right').
 * @param {boolean} props.isOpen - Boolean indicating whether the drawer is open or closed.
 * @param {function} props.onDrawerClose - Function to handle the closing of the drawer.
 * @param {boolean} props.isBackdropOff - Boolean to handle backdrop click to close the drawer.
 * @param {boolean} props.isDrawerCustomStyles - Boolean to customize and add conditional styling.
 * @param {React.ReactNode} props.children - Children elements to be rendered inside the drawer.
 * @param {string} props.styles - custom style of drawer.
 * @param {string} props.childContainerClass - Class for custom styling the children.
 * @param {boolean} props.isHideCloseBtn - Boolean to hide the drawer close button.
 * @param {boolean} props.isShowTopLine - Boolean to show the top line of drawer to on touch move.
 * @param {boolean} props.isHideOverlay - Boolean to hide the drawer main container.
 * @param {boolean} props.isPreventUnlockBody - Boolean to prevent the body from being unlocked.
 * @param {boolean} props.isCleanUp - Deprecated; body unlock always pairs with lockBody on open cleanup.
 * @param {(isOutsideClick: boolean) => boolean} [props.onBeforeClose] - When provided, called before close animation; return false to cancel close (drawer stays open).
 * @param {boolean} [props.isFlatTop] - When true, bottom drawer uses square top corners (no rounded-t-24).
 * @param {string} [props.closeButtonClass] - Appended to the close button, to override its default size/offset utilities.
 * @param {number} [props.closeIconSize] - Rendered size of the close icon in px.
 * @returns {JSX.Element} Drawer component.
 */
const Drawer = ({
  position,
  children,
  isOpen,
  isBackdropOff = true,
  onDrawerClose,
  onBeforeClose,
  styles,
  isDrawerCustomStyles = true,
  childContainerClass = "",
  isHideCloseBtn = false,
  isShowTopLine = false,
  isHideOverlay = true,
  isPreventUnlockBody = false,
  customWidth = "",
  isBackButtonEnabled = false,
  onBackButtonClick,
  headingText = "",
  zIndex = 999,
  drawerId = "",
  justifyContent = "justify-center",
  headingClass = "",
  isFlatTop = false,
  closeButtonClass = "",
  closeIconSize = 60,
}) => {
  const isEmbeddedInPreCheckout = usePreCheckoutEmbedded();
  const CLOSE_ANIMATION_MS = 300;
  // Always start closed visually so open can slide in; `isOpen` drives mount + animation.
  const [shouldRender, setShouldRender] = useState(Boolean(isOpen));
  const [showOverlay, setShowOverlay] = useState(false);
  const drawerRef = useRef(null);
  const diffPercentageRef = useRef(null);
  const dragStartY = useRef(null);
  const posRef = useRef(null);
  const dragThreshold = 30;
  const bottomDrawerRounding = isDrawerCustomStyles && !isFlatTop ? "rounded-t-24" : "";
  const bottomDrawerPadding = isDrawerCustomStyles ? "p-16" : "";
  const positionList = {
    bottom: {
      classes: `flex flex-col ${justifyContent} fixed bottom-0 left-0 right-0 bg-white-900 shadow-inner ${bottomDrawerRounding} ${bottomDrawerPadding} px-0 ${styles}`,
      closeButton: "flex items-center justify-center absolute -top-40 left-[50%] translate-x-[-50%] w-32 h-32 z-1",
      transform: showOverlay ? "translateY(0)" : "translateY(100%)",
    },
    right: {
      classes: `fixed top-0 right-0 h-screen ${customWidth ? customWidth : "w-[29%]"} bg-white-900 shadow-lg p-0 ${styles}`,
      closeButton: "-ml-64 flex items-center justify-center absolute mt-16 w-32 h-32",
      transform: showOverlay ? "translateX(0)" : "translateX(100%)",
    },
  };

  const { classes, closeButton, transform } = positionList[position];

  /**
   * Mount/unmount + body lock. Keep showOverlay=false here so the drawer commits in the
   * closed transform first; a follow-up effect slides it open after that paint.
   */
  useEffect(() => {
    if (isEmbeddedInPreCheckout) {
      return;
    }

    let closeTimer;
    let hasLockedBody = false;

    if (isOpen) {
      setShouldRender(true);
      setShowOverlay(false);
      // Skip lock when parent manages body scroll (e.g. reminder stack via isPreventUnlockBody).
      if (!isPreventUnlockBody) {
        lockBody();
        hasLockedBody = true;
      }
    } else {
      setShowOverlay(false);
      // Keep mounted through the slide-out so programmatic isOpen=false (after actions)
      // animates the same way as the close button.
      closeTimer = setTimeout(() => {
        setShouldRender(false);
      }, CLOSE_ANIMATION_MS);
    }

    return () => {
      clearTimeout(closeTimer);
      // Always release the lock this open cycle acquired — covers X close and
      // programmatic isOpen=false (e.g. MobileLocationLock pincode select).
      if (hasLockedBody) {
        unlockBody();
      }
    };
  }, [isOpen, isPreventUnlockBody, isEmbeddedInPreCheckout]);

  /**
   * Slide open only after the closed state has mounted/painted. Scheduling showOverlay in the
   * same turn as shouldRender lets React commit both together — open then pops with no transition
   * (especially noticeable on desktop right drawers).
   */
  useEffect(() => {
    if (isEmbeddedInPreCheckout || !isOpen || !shouldRender) {
      return;
    }

    let openFrame = requestAnimationFrame(() => {
      openFrame = requestAnimationFrame(() => {
        setShowOverlay(true);
      });
    });

    return () => {
      if (openFrame) {
        cancelAnimationFrame(openFrame);
      }
    };
  }, [isOpen, shouldRender, isEmbeddedInPreCheckout]);

  /**
   * To close the drawer.
   */
  const hideDrawerOverlay = (isOutsideClick = false) => {
    if (typeof onBeforeClose === "function" && onBeforeClose(isOutsideClick) === false) {
      return;
    }
    // Body unlock runs in the isOpen effect cleanup when the parent sets isOpen=false
    // after onDrawerClose — keeps reference-counted locks correct for stacked drawers.
    if (isHideOverlay) {
      setShowOverlay(false);
    }
    setTimeout(() => {
      onDrawerClose(isOutsideClick);
    }, CLOSE_ANIMATION_MS);
  };

  /**
   * Handle closing the drawer.
   * @param {Object} event - Event object.
   */
  const onClose = (event) => {
    event.stopPropagation();
    if (isBackdropOff) {
      hideDrawerOverlay(false);
    }
  };

  /**
   * Handle backdrop click to close the drawer.
   */
  const onBackDropClick = () => {
    if (isOpen && isBackdropOff) {
      hideDrawerOverlay(true);
    }
  };

  /**
   * This function will update the position of drawer on touch.
   * @param {*} clientY
   */
  const updatePosition = (clientY) => {
    const slideDiff = clientY - dragStartY.current;
    const isPositiveDir = slideDiff > 0;

    if (drawerRef.current) {
      const diffPer = (Math.abs(slideDiff) / drawerRef.current.offsetWidth) * 100;
      if (isPositiveDir) {
        if (diffPer <= 100) {
          diffPercentageRef.current = diffPer;
          drawerRef.current.style.transform =
            position === BOTTOM ? `translateY(${diffPer}%)` : `translateX(${diffPer}%)`;
        }
      }
    }
  };

  /**
   * This function will update the position of drawer on touch.
   * @param {*} evt
   */
  const onTouchMove = (evt) => {
    const touch = evt.targetTouches[0];
    const pos = touch.clientY - dragStartY.current;
    posRef.current = pos;
    if (drawerRef.current) {
      drawerRef.current.style.transitionProperty = "none";
    }
    updatePosition(touch.clientY);
  };

  /**
   * It will trigger when we start the touch on the top line of drawer.
   * @param {*} e
   */
  const onHandleTouchStart = (e) => {
    dragStartY.current = e.targetTouches[0].clientY;
    diffPercentageRef.current = 0;

    window.addEventListener("touchmove", onTouchMove);
  };

  /**
   * This function will close the drawer when it reached at certain position.
   */
  const onDragEnd = () => {
    if (drawerRef.current) {
      drawerRef.current.style.transitionProperty = "transform";
    }
    if (posRef.current > 0 && diffPercentageRef.current > dragThreshold) {
      if (drawerRef.current) {
        drawerRef.current.style.transform = position === BOTTOM ? `translateY(100%)` : `translateX(100%)`;
      }
      setTimeout(() => {
        hideDrawerOverlay(false);
      }, 0);
    } else if (drawerRef.current) {
      drawerRef.current.style.transform = position === BOTTOM ? `translateY(0)` : `translateX(0)`;
    }
    window.removeEventListener("touchmove", onTouchMove);
  };

  if (isEmbeddedInPreCheckout) {
    return isOpen ? (
      <div className={formatClassNames("relative flex h-full min-h-0 w-full flex-col", childContainerClass)}>
        {children}
      </div>
    ) : null;
  }

  return (
    shouldRender && (
      <div id={`${position}_drawer`} className="fixed" data-testid="drawer" style={{ zIndex: zIndex }}>
        {showOverlay && (
          <button
            onClick={onBackDropClick}
            id={`${position}_drawer_btn`}
            className="fixed inset-0 h-full w-full bg-black-900 bg-opacity-60"
            data-testid="backdrop"
          />
        )}
        <div
          {...(drawerId && { id: drawerId })}
          style={{
            transform,
            transition: `transform ${CLOSE_ANIMATION_MS}ms ease-in-out, top ${CLOSE_ANIMATION_MS}ms ease-in-out, height ${CLOSE_ANIMATION_MS}ms ease-in-out`,
            ...(isFlatTop && position === BOTTOM ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : undefined),
          }}
          className={classes}
          ref={drawerRef}
        >
          {showOverlay && isDrawerCustomStyles && !isHideCloseBtn && (
            <button
              onClick={onClose}
              id={`${position}_drawer_btn`}
              className={formatClassNames("h-20 w-20 rounded-full", closeButton, closeButtonClass)}
              data-testid="close-button"
            >
              <CustomImage
                src="/icons/drawer-close-icon-v2.svg"
                alt="drawer-close"
                width={closeIconSize}
                height={closeIconSize}
                sizes="10vw"
                isPriority={false}
              />
            </button>
          )}
          {isShowTopLine && (
            <div
              className="flex items-center justify-center pb-8"
              onTouchStart={onHandleTouchStart}
              onTouchEnd={onDragEnd}
              data-testid="top-line"
            >
              <i className="h-[4px] w-40 rounded-xl bg-grey-300" />
            </div>
          )}
          {(headingText || isBackButtonEnabled) && (
            <div
              className={formatClassNames(
                "flex items-center justify-start gap-12 border-b-1 border-lightGrey p-16",
                headingClass,
              )}
            >
              {isBackButtonEnabled && (
                <ArrowLeftIcon
                  size={24}
                  className="cursor-pointer text-black-900"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (onBackButtonClick) {
                      onBackButtonClick(event);
                    } else {
                      onClose(event);
                    }
                  }}
                />
              )}
              {headingText && (
                <Typography Tag="h2" classes="text-16 font-600 text-black-900">
                  {headingText}
                </Typography>
              )}
            </div>
          )}
          <div className={`relative h-full w-full ${childContainerClass ? childContainerClass : ""}`}>{children}</div>
        </div>
      </div>
    )
  );
};

Drawer.propTypes = {
  position: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onDrawerClose: PropTypes.func.isRequired,
  styles: PropTypes.string,
  isDrawerCustomStyles: PropTypes.bool,
  isBackdropOff: PropTypes.bool,
  childContainerClass: PropTypes.string,
  isHideCloseBtn: PropTypes.bool,
  isShowTopLine: PropTypes.bool,
  isHideOverlay: PropTypes.bool,
  isPreventUnlockBody: PropTypes.bool,
  isCleanUp: PropTypes.bool,
  customWidth: PropTypes.string,
  onBeforeClose: PropTypes.func,
  isBackButtonEnabled: PropTypes.bool,
  onBackButtonClick: PropTypes.func,
  headingText: PropTypes.string,
  zIndex: PropTypes.number,
  drawerId: PropTypes.string,
  justifyContent: PropTypes.string,
  headingClass: PropTypes.string,
  isFlatTop: PropTypes.bool,
  closeButtonClass: PropTypes.string,
  closeIconSize: PropTypes.number,
};

export default Drawer;
